import * as React from 'react';
import { useId } from 'react';

import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useMaskInput } from 'use-mask-input';

import { Button } from 'src/registry/ui/Button';
import { Calendar, type CalendarProps } from 'src/registry/ui/Calendar';
import { Input } from 'src/registry/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { generalValidationMessages } from 'src/registry/utils/messages';
import { storage } from 'src/registry/utils/storage';

type DisabledDateRange = { before: Date; after?: Date } | { before?: Date; after: Date };

type CalendarWithTimeProps = {
    dateISO?: string | null;
    onChange: (date: string) => void;
    placeholder?: string;
    disableTime?: boolean;
    disabled?: boolean;
    disabledDateRange?: DisabledDateRange;
    /** Show month/year selects in the calendar caption instead of a plain label. */
    captionLayout?: CalendarProps['captionLayout'];
};

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
};

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
};

export default function CalendarWithTime({
    dateISO,
    onChange,
    placeholder,
    disableTime,
    disabled,
    disabledDateRange,
    captionLayout,
}: CalendarWithTimeProps) {
    const { formatMessage } = useIntl();
    const id = useId();

    const [opened, setOpened] = React.useState<boolean>(false);

    const date = React.useMemo(() => (dateISO ? new Date(dateISO) : new Date()), [dateISO]);

    const placeholderToDisplay = placeholder
        ? placeholder
        : `${formatMessage(generalValidationMessages.fieldRequiredDate)}...`;

    const locale = storage.getLanguage();
    const formattedDate = dateISO ? date.toLocaleDateString(locale, { ...DATE_FORMAT }) : placeholderToDisplay;
    const formattedTime = dateISO ? date.toLocaleTimeString(locale, { ...TIME_FORMAT }) : '';

    const datetimeMask = useMaskInput({
        mask: 'datetime',
        options: {
            placeholder: '0',
            inputFormat: 'HH:mm',
            outputFormat: 'HH:mm',
            showMaskOnHover: false,
        },
    });

    const [time, setTime] = React.useState<string>(formattedTime);

    const onDateChange = React.useCallback(
        (newDateString?: Date) => {
            const newDate = newDateString ?? new Date();
            // A freshly picked date (no prior value) starts at local midnight; an existing value keeps its time.
            const formattedNewDate = new Date(
                newDate.getFullYear(),
                newDate.getMonth(),
                newDate.getDate(),
                dateISO ? date.getHours() : 0,
                dateISO ? date.getMinutes() : 0,
            );
            onChange(formattedNewDate.toISOString());
        },
        [date, dateISO, onChange],
    );

    React.useEffect(() => {
        if (dateISO) {
            setTime(new Date(dateISO).toLocaleTimeString(locale, TIME_FORMAT));
        }
    }, [dateISO, locale]);

    const onTimeChange = React.useCallback(
        ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
            setTime(value);

            const [hours, minutes] = value.split(':').map(Number);

            if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
                const base = dateISO ? new Date(dateISO) : new Date();

                const updated = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hours, minutes);

                onChange(updated.toISOString());
            }
        },
        [dateISO, onChange],
    );

    return (
        <Popover onOpenChange={(opened) => setOpened(opened)} open={opened}>
            <PopoverTrigger asChild>
                <Button
                    disabled={disabled}
                    variant="outline"
                    className="flex flex-1 justify-start gap-2 p-2 text-base font-normal text-gray-500"
                >
                    {!disableTime && <span>{time}</span>}
                    <span>{formattedDate}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="flex max-h-(--radix-popover-content-available-height) w-auto w-fit flex-col gap-6 overflow-y-auto rounded-xl border p-0 py-4 shadow-sm *:shrink-0"
                align="start"
                side="bottom"
            >
                <Calendar
                    mode="single"
                    selected={date}
                    defaultMonth={date}
                    onSelect={onDateChange}
                    className="bg-transparent px-4"
                    disabled={disabledDateRange}
                    captionLayout={captionLayout}
                />
                {!disableTime && (
                    <div className="w-full max-w-xs space-y-2">
                        <div className="relative flex w-full items-center gap-2 px-4">
                            <FontAwesomeIcon
                                icon={faClock}
                                className="text-muted-foreground pointer-events-none absolute left-6 top-1/2 size-4 -translate-y-1/2"
                            />
                            <Input
                                id={id}
                                type="text"
                                placeholder="00-23:59"
                                value={time}
                                onChange={onTimeChange}
                                ref={datetimeMask}
                                className="pl-10"
                            />
                        </div>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
